import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { Role } from '@gen-common/enums';
import Session from 'supertokens-web-js/recipe/session';

import { AuthService } from './auth.service';

export function roleGuard(expectedRoles: Role[]): CanActivateFn {
  return async (route, state) => {
    const router = inject(Router);
    const authService = inject(AuthService);

    console.debug('[RoleGuard] Checking route access.', {
      url: state.url,
      routePath: route.routeConfig?.path,
      expectedRoles,
      auth: authService.getDebugSnapshot()
    });

    const sessionExists = await authService.ensureSession();
    if (!sessionExists) {
      return false;
    }

    await authService.loadUser(false);
    if (authService.vpnRequired) {
      return false;
    }

    let roles: Role[] = authService.roles as Role[];
    try {
      const payload = await Session.getAccessTokenPayloadSecurely();
      roles = payload?.['st-role']?.v || roles;
    } catch (error) {
      console.warn('[RoleGuard] Failed to read roles from access token payload. Falling back to cached roles.', {
        url: state.url,
        routePath: route.routeConfig?.path,
        expectedRoles,
        auth: authService.getDebugSnapshot(),
        error
      });
    }

    const hasRole = expectedRoles.some((r) => roles.includes(r));
    if (!hasRole) {
      console.warn('[RoleGuard] Role check failed. Redirecting to unauthorized.', {
        url: state.url,
        routePath: route.routeConfig?.path,
        expectedRoles,
        actualRoles: roles,
        auth: authService.getDebugSnapshot()
      });
      return router.createUrlTree(['/unauthorized'], {
        queryParams: {
          from: state.url,
          reason: 'role'
        }
      });
    }

    return true;
  };
}
